import { Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest } from 'rxjs';

import { AdobeAnalyticsService } from 'src/app/modules/core/services/adobe-analytics.service';
import { BrandService } from 'src/app/modules/core/services/brand.service';
import { SettingsService } from 'src/app/modules/core/services/settings.service';

import { AppFacade } from 'src/app/modules/ngrx-store/app/app.facade';

import { ERROR_CODES } from 'src/app/modules/shared/constants/error-codes';
import { NAVIGATION_ROUTES } from 'src/app/modules/shared/constants/route-paths';
import { UnsubscribeOnDestroy } from 'src/app/modules/shared/utilities/unsubscribe-on-destroy';
import { RouterLink } from '@angular/router';
import { HeaderComponent } from 'src/app/modules/core/components/header/header.component';
import { InternalUrlPipe } from 'src/app/modules/shared/pipes/internal-url.pipe';

@Component({
	selector: 'cp-error-page',
	templateUrl: './error-page.component.html',
	styleUrls: ['./error-page.component.scss'],
	imports: [RouterLink, HeaderComponent, InternalUrlPipe]
})
export class ErrorPageComponent extends UnsubscribeOnDestroy implements OnInit, OnDestroy {
	routes = NAVIGATION_ROUTES;

	public ERROR_CODES = ERROR_CODES;

	myAccountUrl: string | undefined;

	tscUrl: string | undefined;

	errorHeader: string | undefined;

	errorText: string | undefined;

	errorBody: string | undefined;

	errorCode: number | null | undefined;

	constructor(private adobeAnalyticsService: AdobeAnalyticsService,
		private appFacade: AppFacade,
		private brandService: BrandService,
		private settingsService: SettingsService) {
		
		super();
		
		combineLatest([
			this.settingsService.getSettings(),
			this.appFacade.errorCode$,
		]).pipe(
			this.takeUntilDestroyed()
		).subscribe(
			([settings, errorCode]) => {
				const brand = this.brandService.getBrand();
				this.myAccountUrl = settings.brandConfigs[brand.name].myAccountUrl;
				this.tscUrl = settings.externalLinks.tscUrl;

				if (!errorCode) {
					// Default to 404 - invalid URL
					this.errorCode = ERROR_CODES.NOT_FOUND;
				} else {
					this.errorCode = errorCode;
				}

				switch (this.errorCode) {
					case ERROR_CODES.AGREEMENT_INVALID:
						this.errorHeader = 'Sales Agreement Not Found';
						this.errorText = `We're sorry we couldn't find a valid sales agreement. You can try again or return to <a href="${this.myAccountUrl}">MyAccount</a> for more information.`;
						break;
					case ERROR_CODES.PORTAL_NOT_ENABLED:
						this.errorHeader = 'Portal Unavailable';
						this.errorText = `We're sorry, but access to the Portal is currently unavailable. Please try again or visit <a href="${this.myAccountUrl}">MyAccount</a> for more information.`;	
						break;
					case ERROR_CODES.TEN_YEARS_POST_CLOSE:
						this.errorHeader = 'PORTAL ACCESS EXPIRED';
						this.errorText = `We're sorry but our records indicate that you are past the 10-year warranty timeframe.`;	
						break;
					case ERROR_CODES.FORBIDDEN:
						this.errorHeader = 'Uh oh! Something went wrong.';
						this.errorText = `Looks like you do not have access to Customer Portal Preview. Please try again or access <a href="${this.tscUrl}">TSC</a> to request access.`;	
						break;
					case ERROR_CODES.IDENTITY_SALES_AGREEMENT_NOT_FOUND:
						this.errorHeader = 'Sales Agreement Not Found';
						this.errorText = `We're sorry we couldn't find a valid sales agreement. You can try again or return to <a href="${this.myAccountUrl}">MyAccount</a> for more resources.`;
						this.errorBody = `<strong>Common reasons for this error:</strong><br>
						<ol>
							<li>Your Sales Agreement was <strong><em>not</em></strong> registered when you created your MyAccount login. You can check this by returning to MyAccount. If your sales agreement is not registered, you will see a section to enter a Last Name and Agreement Number. Please follow the steps below to complete registration:
								<ol type="a">
									<li>Enter the <strong><em>last name of any</em></strong> buyer listed on your sales agreement.</li>
									<li>Enter the <strong><em>agreement number</em></strong> as shown on your sales agreement.</li>
									<li>Click Register Your Home.</li>
									<li>Now attempt to access the Portal. If you still have issues, please work with your Sales Consultant.</li>
								</ol>
							</li>
							<li>Your sales agreement was canceled or void. Portal access is only available for active agreements.</li>
						</ol>`;
						break;
					default:
						this.errorHeader = 'Uh oh! Something went wrong.';
						this.errorText = `We're sorry we encountered an error on our end. You can try again later or return to <a href="${this.myAccountUrl}">MyAccount</a> for more resources.`;	
						break;
				}

				// Set error message based on error code, not found error header is different from the default switch case message
				const evar = { message: this.errorCode === ERROR_CODES.NOT_FOUND ? 'Uh oh! Page not found' : this.errorHeader };

				// Track the error event in Adobe Analytics
				this.adobeAnalyticsService.trackEvent('Error', evar);
			});
	}

	ngOnInit(): void {
		this.appFacade.setDisclaimers([]);
	}

	override ngOnDestroy(): void {
		this.appFacade.resetDisclaimers();
		this.appFacade.resetErrorCode();

		super.ngOnDestroy();
	}
}